/**
 * Compact jump targets for unanswered simulation tasks (used inside incomplete-module AlertDialogs).
 *
 * @module SimulationUnansweredJumpChips
 */

import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { SimulationNavItem } from '@/utils/simulationQuestionNavigation';

export interface SimulationUnansweredJumpChipsProps {
  readonly items: readonly SimulationNavItem[];
  readonly onJump: (itemId: string) => void;
  readonly disabled?: boolean;
  /** When false, chips are display-only (no navigation). Defaults to true. */
  readonly navigationEnabled?: boolean;
  readonly className?: string;
}

export function SimulationUnansweredJumpChips({
  items,
  onJump,
  disabled,
  navigationEnabled = true,
  className,
}: SimulationUnansweredJumpChipsProps) {
  const { t } = useTranslation();

  if (items.length === 0) {
    return null;
  }

  const isInteractive = navigationEnabled && !disabled;

  return (
    <div className={cn('mt-2', className)}>
      <p className="mb-2 text-sm text-muted-foreground font-ui">
        {navigationEnabled
          ? t('simulation.active.unansweredJumpHint', { defaultValue: 'Jump to:' })
          : t('simulation.active.unansweredListHint', { defaultValue: 'Unanswered:' })}
      </p>
      <fieldset className="m-0 min-w-0 border-0 p-0">
        <legend className="sr-only">
          {navigationEnabled
            ? t('simulation.active.unansweredJumpGroupAriaLabel', {
                defaultValue: 'Jump to unanswered items',
              })
            : t('simulation.active.unansweredListGroupAriaLabel', {
                defaultValue: 'Unanswered items',
              })}
        </legend>
        <div className="flex flex-wrap gap-2">
        {items.map((item) => (
          <Button
            key={item.id}
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled}
            className={cn(
              'min-h-9 shrink-0 font-ui',
              !isInteractive && 'pointer-events-none cursor-default hover:bg-background',
              item.isFlagged && 'border-exam-flag/30 bg-exam-flag/5 text-exam-flag',
            )}
            onClick={() => {
              if (isInteractive) {
                onJump(item.id);
              }
            }}
          >
            {item.label}
          </Button>
        ))}
        </div>
      </fieldset>
    </div>
  );
}
